var __rest = this && this.__rest || function (s, e) {
  var t = {};

  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];

  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};

import * as React from 'react';
import { useCallback, useContext, useState, useEffect, useMemo } from 'react';
export const RouterContext = React.createContext(null);

function useRouterContext() {
  return useContext(RouterContext);
}

let idCounter = 0;
export function useRouter(beforeUnload) {
  const context = useRouterContext();
  if (!context) throw new Error("Can't find context. Add <Router /> to the root of your project");
  const id = useMemo(() => idCounter++, []);
  const {
    path,
    routes,
    addBeforeUnload,
    beforeUnloads,
    clearBeforeUpload,
    setPath
  } = context;
  usePopState(clearBeforeUpload);
  if (beforeUnload) addBeforeUnload(id, beforeUnload);
  const [route, params] = getRouteByUrl(routes, path);
  const pushRoute = useCallback((routeName, routeParams) => {
    const promises = Object.keys(beforeUnloads).map(key => beforeUnloads[key]).map(beforeUnload => {
      return new Promise(resolve => {
        beforeUnload(resolve);
      });
    });
    Promise.all(promises).then(() => {
      navigateByUrl(getUrlByRoute(routes, routeName, routeParams), setPath);
      clearBeforeUpload();
    });
  }, [routes, beforeUnloads]);
  return {
    params: params || {},
    routeParams: params || {},
    routeName: route ? route.name : '',
    route: route ? route.name : '',
    pushRoute
  };
}

function Router(props) {
  const {
    routes,
    notFoundPage
  } = props;
  const [path, setPath] = useState(getCurrentPath());
  const [beforeUnloads, setBeforeUnloads] = useState({});
  const storedRoutes = connect(routes);
  usePopState(path => setPath(path));
  const [route] = getRouteByUrl(storedRoutes, path);
  const addBeforeUnload = useCallback((id, beforeUnload) => {
    if (beforeUnload) {
      if (beforeUnloads[id] !== beforeUnload) {
        setBeforeUnloads(Object.assign(Object.assign({}, beforeUnloads), {
          [id]: beforeUnload
        }));
      }
    }
  }, [beforeUnloads]);
  const clearBeforeUpload = useCallback(() => Object.keys(beforeUnloads).length && setBeforeUnloads({}), [beforeUnloads]);
  if (!route) return notFoundPage || null;
  if (props.onLoadPage) props.onLoadPage(route.name);
  return React.createElement(RouterContext.Provider, {
    value: {
      path,
      routes: storedRoutes,
      beforeUnloads,
      setPath,
      addBeforeUnload,
      clearBeforeUpload
    }
  }, React.createElement(route.page, {}, null));
}

export const Link = props => {
  const {
    route,
    params = {}
  } = props,
        otherProps = __rest(props, ["route", "params"]);

  const context = useRouterContext();
  if (!context) return null;
  const {
    beforeUnloads,
    routes,
    setPath,
    clearBeforeUpload
  } = context;
  const onClick = React.useCallback(e => {
    try {
      if (props.onClick) props.onClick(e);
    } catch (ex) {
      e.preventDefault();
      throw ex;
    }

    if (props.target && ['_blank', '_parent', '_top'].includes(props.target)) {
      return;
    }

    const href = e.currentTarget.href;

    if (shouldTrap(e)) {
      e.preventDefault();
      const promises = Object.keys(beforeUnloads).map(key => beforeUnloads[key]).map(beforeUnload => {
        return new Promise(resolve => {
          beforeUnload(resolve);
        });
      });
      Promise.all(promises).then(() => {
        navigateByUrl(href, setPath);
        clearBeforeUpload();
      });
    }
  }, [props]);
  return React.createElement('a', Object.assign(Object.assign({}, otherProps), {
    onClick,
    href: getUrlByRoute(routes, route, params)
  }));
};

function toQueryString(params) {
  let queryString = '';

  for (const key in params) {
    if ({}.hasOwnProperty.call(params, key) && params[key] != null) {
      if (queryString) {
        queryString += '&';
      }

      const value = params[key];
      queryString += encodeURIComponent(key) + (value === '' ? '' : '=' + encodeURIComponent(value));
    }
  }

  return queryString;
}

function objectValues(object) {
  return Object.keys(object).map(key => object[key]);
}

export function connect(routes) {
  const objRoutes = {};
  routes.forEach(route => {
    let pattern = route.path;
    const keys = {};
    pattern = pattern.split('/').join('\\/');
    const matches = pattern.match(/([:*]([a-zA-Z][a-zA-Z0-9_-]*))/g);
    if (matches) matches.forEach((match, index) => {
      pattern = pattern.replace(match, match[0] === ':' ? '([^/]+)' : '(.+?)');
      keys[match.substring(1)] = index;
    });
    objRoutes[route.name] = Object.assign(Object.assign({}, route), {
      pattern,
      keys
    });
  });
  return objRoutes;
}

function navigateByUrl(url, setPath) {
  window.history.pushState(null, '', url);
  dispatchEvent(new PopStateEvent('popstate', undefined));
  setPath(getCurrentPath());
}

export function getUrlByRoute(routes, routeName, routeParams) {
  const route = routes[routeName];
  if (!route) throw new Error(`Unknown route: '${routeName}'`);
  let url = route.path;
  const queryStringParams = {};
  if (routeParams) Object.keys(routeParams).forEach(paramKey => {
    const paramValue = routeParams[paramKey];

    if (route.keys && route.keys[paramKey] !== undefined) {
      url = url.replace(new RegExp(`[:*]${paramKey}`), paramValue.toString());
    } else {
      createQueryStringParams(paramKey, paramValue).forEach(param => {
        queryStringParams[param.key] = param.value;
      });
    }
  });
  return url + (Object.keys(queryStringParams).length ? '?' + toQueryString(queryStringParams) : '');
}

function createQueryStringParams(baseKey, value) {
  let params = [];

  if (typeof value === 'object') {
    for (let key in value) {
      params = [...params, ...createQueryStringParams(`${baseKey}.${key}`, value[key])];
    }
  } else {
    params.push({
      key: baseKey,
      value
    });
  }

  return params;
}

export function getRouteByUrl(routes, url) {
  const [urlPath, queryStrings] = url.split('?');
  const params = {};
  const route = objectValues(routes).find(route => {
    const pattern = new RegExp(`^${route.pattern}$`);
    const matches = urlPath.match(pattern);
    const {
      keys
    } = route;

    if (matches) {
      if (keys) {
        const [, ...paramsValues] = matches;
        paramsValues.forEach((value, index) => {
          const key = Object.keys(keys).find(key => keys[key] === index);
          if (key) params[key] = value;
        });
      }

      if (queryStrings) queryStrings.split('&').forEach(pair => {
        const [key, value] = pair.split('=');
        setValueByPath(params, key, value);
      });
      return true;
    }
  });

  if (route) {
    return [route, params];
  }

  return [];
}

function setValueByPath(obj, path, value) {
  const a = path.split(/[^\\]\./);
  let o = obj;

  while (a.length - 1) {
    const currentValue = a.shift();
    if (!currentValue) continue;

    if (!(currentValue in o)) {
      if (parseInt(a[0], 10).toString() === a[0]) {
        o[currentValue] = [];
      } else {
        o[currentValue] = {};
      }
    }

    o = o[currentValue];
  }

  o[a[0]] = value ? value.replace('\\', '') : value;
}

function shouldTrap(e) {
  return !e.defaultPrevented && e.button === 0 && !(event.metaKey || event.altKey || event.ctrlKey || event.shiftKey);
}

function getCurrentPath() {
  return window.location.pathname + window.location.search || '/';
}

function usePopState(setFn) {
  useEffect(() => {
    const onPopState = () => {
      setFn(getCurrentPath());
    };

    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [setFn]);
}

export default Router;